import {
  Avatar,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  IconButton,
  Paper,
  PaperProps,
  Typography,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  Popover,
  Badge,
} from "@mui/material";
import {
  BorderAll as BorderIcon,
  Palette as ColorIcon,
  Category as ShapeIcon,
  FormatSize as SizeIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

import CancelIcon from "@mui/icons-material/Cancel";
import CheckIcon from "@mui/icons-material/Check";
import Box from "@mui/material/Box";
import * as React from "react";
import { useState } from "react";
import Draggable from "react-draggable";
import { useTranslation } from "react-i18next";
import { StoneControl } from "../controls/StoneControl";
import { Game, StoneType } from "../models";
import { getGameConfig } from "../utils/gameConfig";
import { GameBoardControl } from "./Game/GameBoardControl";
import { useTheme as useAppTheme } from "../contexts/ThemeContext";

export interface SelectStoneDialogProps {
  open: boolean;
  onClose: (value: StoneType) => void;
  stones: StoneType[];
  game: Game;
  myTurn?: boolean;
  player?: number;
}

export function PaperComponent(props: PaperProps) {
  const theme = useTheme();
  const xs = useMediaQuery(theme.breakpoints.down("md"));

  let sx = {};
  if (xs) {
    sx = {
      minHeight: xs ? "100vh !important" : "100%",
      minWidth: xs ? "100vw !important" : "100%",
      maxHeight: xs ? "100vh !important" : "100%",
      maxWidth: xs ? "100vw !important" : "100%",
      margin: xs ? 0 : "",
    };
  }

  return (
    <Draggable
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} sx={{ m: "0 !important", p: 0, ...sx }} />
    </Draggable>
  );
}

interface StoneFilter {
  color: 0 | 1 | null;
  size: 0 | 1 | null;
  variant: 0 | 1 | null;
  shape: 0 | 1 | null;
}

export function SelectStoneDialog(props: SelectStoneDialogProps) {
  const { onClose, open, stones, game, myTurn, player } = props;
  const { t } = useTranslation();
  const { SHOW_CONFIRM_BUTTONS } = getGameConfig();
  const { themeConfig } = useAppTheme();

  // Helper to get personalized player name
  const getPlayerName = (isCurrentPlayer: boolean) => {
    if (isCurrentPlayer) {
      return t("gameControl:you"); // "Du"
    }
    // Get opponent's name
    const opponentName = game.currentPlayer === 1 ? game.player1 : game.player2;
    return opponentName || t("gameControl:opponent"); // "Gegner"
  };

  console.log(
    "[SelectStoneDialog] RENDER - open:",
    open,
    "stones.length:",
    stones.length
  );

  const remainingStones = getRemainingStones(game, stones);
  console.log(
    "[SelectStoneDialog] remainingStones.length:",
    remainingStones.length
  );

  const [filteredStones, setFilteredStones] =
    useState<StoneType[]>(remainingStones);
  const [showConfirm, setShowConfirm] = useState<string | null>(null); // Use stone key instead of index
  const [toggleBoard, setToggleBoard] = useState<boolean>(false);

  console.log(
    "[SelectStoneDialog] filteredStones.length:",
    filteredStones.length
  );

  // Update filteredStones when dialog opens
  React.useEffect(() => {
    console.log(
      "[SelectStoneDialog] useEffect triggered - open:",
      open,
      "remainingStones.length:",
      remainingStones.length
    );
    if (open) {
      console.log(
        "[SelectStoneDialog] Dialog opened - setting filteredStones to remainingStones"
      );
      setFilteredStones(remainingStones);
      setShowConfirm(null); // Reset confirmation when dialog opens
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleFiltered = React.useCallback(
    (filters: StoneFilter) => {
      console.log(
        "[SelectStoneDialog] handleFiltered called with filters:",
        filters
      );
      const isIconSet = game?.iconSet !== null && game?.iconSet !== undefined;
      const filteredStones = remainingStones.filter((stone) => {
        const matchesColor =
          filters.color === null || stone.color === filters.color;
        const matchesSize =
          filters.size === null || stone.size === filters.size;

        if (isIconSet) {
          // Icon-basierte Steine: ursprüngliche Zuordnung beibehalten
          const matchesVariant =
            filters.variant === null || stone.variant === filters.variant;
          const matchesShape =
            filters.shape === null || stone.shape === filters.shape;

          return matchesColor && matchesSize && matchesVariant && matchesShape;
        }

        // Default-Stones (Theme-Steine):
        // - Variante (Filter) = rund / eckig  -> wirkt auf SHAPE
        // - Umrandung (Filter) = mit / ohne  -> wirkt auf VARIANT (Ring vs voll)
        const matchesVariantAsShape =
          filters.variant === null || stone.shape === filters.variant;

        const effectiveVariantFilter =
          filters.shape === null ? null : filters.shape === 0 ? 1 : 0; // 0 („mit“) => variant 1 (Ring), 1 („ohne“) => variant 0 (voll)

        const matchesShapeAsVariant =
          effectiveVariantFilter === null ||
          (stone.variant ?? 0) === effectiveVariantFilter;

        return (
          matchesColor &&
          matchesSize &&
          matchesVariantAsShape &&
          matchesShapeAsVariant
        );
      });
      console.log(
        "[SelectStoneDialog] Filtered stones count:",
        filteredStones.length
      );
      setFilteredStones(filteredStones);
    },
    [remainingStones, game]
  );

  const confirmStoneSelection = (stone: StoneType) => {
    console.log(
      "✅ [SelectStoneDialog] confirmStoneSelection called with stone:",
      {
        key: stone.key,
        name: stone.name,
        color: stone.color,
        size: stone.size,
        shape: stone.shape,
        variant: stone.variant,
        fullStone: stone,
      }
    );
    onClose(stone);
    console.log("✅ [SelectStoneDialog] onClose called with stone:", stone);
  };

  return (
    <Dialog
      onClose={() => {}}
      open={open}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          minHeight: "650px",
          maxHeight: "650px",
          borderRadius: 3,
          background: themeConfig.dialog.background,
          border: themeConfig.dialog.border,
        },
      }}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
    >
      <DialogTitle
        style={{ cursor: "move" }}
        sx={{
          borderBottom: themeConfig.dialog.headerBorder,
          background: themeConfig.dialog.headerBackground,
          pb: 2,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} color="primary.main">
            {t("gameControl:chooseStoneTitle")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {/* Dialog opens when !myTurn, which means I need to choose a stone */}
            {!myTurn
              ? t("gameControl:youChooseNextStone")
              : t("gameControl:playerChoosesNextStone", {
                  player: getPlayerName(false),
                })}
          </Typography>
        </Box>
      </DialogTitle>

      {/* Toggle Button with Filter Button */}
      <Box
        mb={2}
        mt={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        px={2}
      >
        {/* Filter Button (always available, also on board view) */}
        <Box sx={{ width: 40, display: "flex", justifyContent: "flex-start" }}>
          <FilterStones
            handleFiltered={handleFiltered}
            stones={stones}
            game={game}
            filteredCount={filteredStones.length}
          />
        </Box>

        {/* Center Button */}
        <Button
          variant="contained"
          onClick={() => setToggleBoard(!toggleBoard)}
          sx={{
            bgcolor: "primary.main",
            color: "primary.contrastText",
            fontWeight: 600,
            "&:hover": {
              bgcolor: "primary.dark",
            },
          }}
        >
          {toggleBoard
            ? t("gameControl:chooseStoneButton")
            : t("gameControl:showBoardButton")}
        </Button>

        {/* Right Spacer for balance */}
        <Box sx={{ width: 40 }} />
      </Box>

      {/* Content Container - Fixed height for both views */}
      <Box
        sx={{
          height: "480px",
          position: "relative",
          px: { xs: 2, sm: 3 },
          pb: 2,
        }}
      >
        {/* Board View */}
        {toggleBoard && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <GameBoardControl
              game={game}
              boardOnly={true}
              emptyBoard={false}
              sx={{
                transform: "scale(0.85)",
                transformOrigin: "center",
              }}
            />
          </Box>
        )}

        {/* Stone Selection View */}
        {!toggleBoard && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            {/* Show stones to select */}
            {filteredStones.length > 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  gap: { xs: 1.5, sm: 2 },
                  p: { xs: 2.5, sm: 3 },
                  background: themeConfig.board.background,
                  borderRadius: 3,
                  border: themeConfig.board.border,
                  boxShadow: themeConfig.board.boxShadow,
                  maxWidth: "480px",
                  width: "100%",
                  position: "relative",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    inset: -4,
                    background: themeConfig.board.beforeBackground,
                    borderRadius: 3,
                    zIndex: -1,
                  },
                }}
              >
                {filteredStones.map((stone, index) => (
                  <Box
                    key={`stone-${stone.key}`}
                    onClick={() => {
                      const stoneKeyStr = String(stone.key ?? "");
                      if (showConfirm !== stoneKeyStr) {
                        if (SHOW_CONFIRM_BUTTONS) {
                          setShowConfirm(stoneKeyStr);
                        } else {
                          console.log("🎯 [SelectStoneDialog] Clicked stone:", {
                            index,
                            stoneKey: stone.key,
                            stoneName: stone.name,
                            stone: stone,
                          });
                          confirmStoneSelection(stone);
                        }
                      }
                    }}
                    sx={{
                      width: { xs: "75px", sm: "82px" },
                      height: { xs: "75px", sm: "82px" },
                      background: themeConfig.tile.background,
                      border: "3px solid",
                      borderColor:
                        showConfirm === String(stone.key ?? "")
                          ? "primary.main"
                          : themeConfig.tile.border.split(" ")[2] || "divider",
                      borderRadius: 2,
                      boxShadow: themeConfig.tile.boxShadow,
                      cursor: "pointer",
                      transition: "all 0.25s ease-out",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      position: "relative",
                      "&:hover": {
                        border: themeConfig.tile.hoverBorder,
                        boxShadow: themeConfig.tile.hoverBoxShadow,
                        transform: "translateY(-2px) scale(1.05)",
                      },
                    }}
                  >
                    {showConfirm === String(stone.key ?? "") ? (
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log(
                              "✅ [SelectStoneDialog] Confirm button clicked for stone:",
                              {
                                stoneKey: stone.key,
                                stoneName: stone.name,
                                stone: stone,
                              }
                            );
                            confirmStoneSelection(stone);
                          }}
                          sx={{ bgcolor: "success.dark" }}
                        >
                          <CheckIcon sx={{ color: "success.main" }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowConfirm(null);
                          }}
                          sx={{ bgcolor: "error.dark" }}
                        >
                          <CancelIcon sx={{ color: "error.main" }} />
                        </IconButton>
                      </Box>
                    ) : (
                      <StoneControl stone={stone} iconSet={game?.iconSet} />
                    )}
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {t("gameControl:noStonesAvailable")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  {t("gameControl:adjustFilters")}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Dialog>
  );
}

function FilterStones({
  stones,
  handleFiltered,
  game,
  filteredCount,
}: {
  stones: StoneType[];
  handleFiltered: (filters: StoneFilter) => void;
  game: Game;
  filteredCount: number;
}) {
  const { t } = useTranslation();
  const muiTheme = useTheme();
  const { themeConfig } = useAppTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));

  const [filters, setFilters] = useState<StoneFilter>({
    color: null,
    size: null,
    variant: null,
    shape: null,
  });
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  React.useEffect(() => {
    handleFiltered(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleFilterToggle = (key: keyof typeof filters, value: any) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [key]: prevFilters[key] === value ? null : value,
    }));
  };

  const filterOptions = [
    {
      label: "colorLabel",
      icon: <ColorIcon />,
      key: "color" as keyof StoneFilter,
    },
    {
      label: "sizeLabel",
      icon: <SizeIcon />,
      key: "size" as keyof StoneFilter,
    },
    {
      label: "variantLabel",
      icon: <ShapeIcon />,
      key: "variant" as keyof StoneFilter,
    },
    {
      label: "shapeLabel",
      icon: <BorderIcon />,
      key: "shape" as keyof StoneFilter,
    },
  ];

  const activeFiltersCount = Object.values(filters).filter(
    (v) => v !== null
  ).length;

  const open = Boolean(anchorEl);

  return (
    <>
      {/* Filter Button */}
      <Badge badgeContent={activeFiltersCount} color="warning">
        <IconButton
          size="medium"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            border: "1px solid",
            borderColor: activeFiltersCount > 0 ? "primary.main" : "divider",
            bgcolor: activeFiltersCount > 0 ? "primary.main" : "action.hover",
            color:
              activeFiltersCount > 0 ? "primary.contrastText" : "primary.main",
            opacity: activeFiltersCount > 0 ? 0.9 : 0.7,
            "&:hover": {
              bgcolor:
                activeFiltersCount > 0 ? "primary.dark" : "action.selected",
              borderColor: "primary.main",
            },
            transition: "all 0.2s",
          }}
        >
          <FilterIcon fontSize="small" />
        </IconButton>
      </Badge>

      {/* Filter Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxWidth: isMobile ? "90vw" : 400,
            boxShadow: 6,
            mt: 1,
            background: themeConfig.dialog.background,
            border: themeConfig.dialog.border,
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              mb: 2,
              pb: 2,
              borderBottom: "1px solid",
              borderColor: "divider",
              gap: 1,
            }}
          >
            <Box>
              <Typography variant="body2" fontWeight={600} color="primary.main">
                {t("gameControl:filterStones")}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.25, display: "block" }}
              >
                {filteredCount}{" "}
                {t("gameControl:remainingStonesTitle").toLowerCase()}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setAnchorEl(null)}
              sx={{ color: "primary.main" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Filter Grid */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: 1.5,
              mb: activeFiltersCount > 0 ? 2 : 0,
            }}
          >
            {filterOptions.map((filterOption) => (
              <Box key={filterOption.key}>
                <ToggleButtonGroup
                  value={filters[filterOption.key]}
                  exclusive
                  orientation="vertical"
                  onChange={(_, value) => {
                    if (value !== null) {
                      handleFilterToggle(filterOption.key, value);
                    } else {
                      setFilters((prev) => ({
                        ...prev,
                        [filterOption.key]: null,
                      }));
                    }
                  }}
                  size="small"
                  fullWidth
                  sx={{
                    "& .MuiToggleButton-root": {
                      borderRadius: 1,
                      textTransform: "none",
                      px: 1,
                      py: 0.75,
                      color: "primary.main",
                      borderColor: "divider",
                      "&.Mui-selected": {
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                        opacity: 0.9,
                        borderColor: "primary.main",
                        "&:hover": {
                          bgcolor: "primary.dark",
                        },
                      },
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    },
                  }}
                >
                  {/* Filter Label */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mb: 0.5,
                    }}
                  >
                    {React.cloneElement(
                      filterOption.icon as React.ReactElement,
                      {
                        sx: { fontSize: 16, color: "primary.main" },
                      }
                    )}
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color="primary.main"
                    >
                      {t(
                        `translation:StoneSelectToolTips.${filterOption.label}`
                      )}
                    </Typography>
                  </Box>
                  {[0, 1].map((value) => {
                    const isIconSet =
                      game?.iconSet !== null && game?.iconSet !== undefined;

                    const baseDefaultStone = {
                      color: 0,
                      size: 0,
                      variant: 0,
                      shape: 1,
                    };

                    let stoneCriteria: Partial<StoneType> = {
                      ...baseDefaultStone,
                    } as Partial<StoneType>;

                    if (isIconSet) {
                      // Icon-Set: Filter direkt auf das jeweilige Attribut anwenden
                      stoneCriteria = {
                        ...stoneCriteria,
                        [filterOption.key]: value,
                      } as Partial<StoneType>;
                    } else {
                      // Default-Stones (Theme-Steine):
                      // Variante = rund/eckig  -> wirkt auf SHAPE
                      // Umrandung = mit/ohne  -> wirkt auf VARIANT (Ring vs voll)
                      if (filterOption.key === "variant") {
                        stoneCriteria = {
                          ...stoneCriteria,
                          shape: value as 0 | 1, // 0 = eckig, 1 = rund
                          variant: 0, // volle Steine zur besseren Erkennbarkeit
                        };
                      } else if (filterOption.key === "shape") {
                        // 0 („mit“) => variant 1 (Ring), 1 („ohne“) => variant 0 (voll)
                        stoneCriteria = {
                          ...stoneCriteria,
                          shape: 1,
                          variant: value === 0 ? 1 : 0,
                        };
                      } else {
                        stoneCriteria = {
                          ...stoneCriteria,
                          [filterOption.key]: value,
                        } as Partial<StoneType>;
                      }
                    }

                    const foundStone = stones.find((s) =>
                      Object.entries(stoneCriteria).every(
                        ([key, val]) => s[key as keyof StoneType] === val
                      )
                    );
                    const stone = (foundStone || baseDefaultStone) as StoneType;

                    return (
                      <ToggleButton key={value} value={value}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: "white",
                              width: 24,
                              height: 24,
                            }}
                          >
                            <StoneControl
                              stone={stone}
                              iconSet={game?.iconSet}
                            />
                          </Avatar>
                          <Typography variant="caption">
                            {t(
                              `translation:StoneSelectToolTips.${filterOption.key}Label${value}`
                            )}
                          </Typography>
                        </Box>
                      </ToggleButton>
                    );
                  })}
                </ToggleButtonGroup>
              </Box>
            ))}
          </Box>

          {/* Active Filters */}
          {activeFiltersCount > 0 && (
            <Box
              sx={{
                pt: 2,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.5,
                  mb: 1,
                }}
              >
                {Object.entries(filters).map(([key, value]) => {
                  if (value !== null) {
                    return (
                      <Chip
                        key={key}
                        size="small"
                        label={t(
                          `translation:StoneSelectToolTips.${key}Label${value}`
                        )}
                        onDelete={() =>
                          setFilters((prev) => ({ ...prev, [key]: null }))
                        }
                        sx={{
                          bgcolor: "action.hover",
                          color: "primary.main",
                          borderColor: "divider",
                          "& .MuiChip-deleteIcon": {
                            color: "primary.main",
                            "&:hover": {
                              color: "primary.light",
                            },
                          },
                        }}
                        variant="outlined"
                      />
                    );
                  }
                  return null;
                })}
              </Box>
              <Button
                size="small"
                fullWidth
                variant="contained"
                onClick={() =>
                  setFilters({
                    color: null,
                    size: null,
                    variant: null,
                    shape: null,
                  })
                }
                sx={{
                  textTransform: "none",
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  "&:hover": {
                    bgcolor: "primary.dark",
                  },
                }}
              >
                {t("gameControl:clearFilters")}
              </Button>
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
}

function getRemainingStones(game: Game, stones: StoneType[]) {
  const placedStones = game.board
    ?.filter((field) => {
      return field?.stone;
    })
    .map((f) => f?.stone?.key);

  const remainingStones = stones
    .filter((s) => {
      return (
        !placedStones ||
        placedStones.length === 0 ||
        placedStones.indexOf(s.key) === -1
      );
    })
    .map((s) => {
      return {
        name: s.name,
        key: s.key,
        color: s.color,
        size: s.size,
        shape: s.shape,
        variant: s.variant,
        img: s.img,
      };
    });
  return remainingStones;
}

export function RemainingStonesControl(props: {
  game: Game;
  stones: StoneType[];
}) {
  const { t } = useTranslation();
  const { themeConfig } = useAppTheme();
  const { game, stones } = props;

  const remainingStones = getRemainingStones(game, stones);
  const [filteredStones, setFilteredStones] =
    useState<StoneType[]>(remainingStones);

  // Update filteredStones when remainingStones change
  React.useEffect(() => {
    if (filteredStones.length === 0) {
      setFilteredStones(remainingStones);
    }
  }, [remainingStones, filteredStones.length]);

  const handleFiltered = React.useCallback(
    (filters: StoneFilter) => {
      const isIconSet = game?.iconSet !== null && game?.iconSet !== undefined;
      const filteredStones = remainingStones.filter((stone) => {
        const matchesColor =
          filters.color === null || stone.color === filters.color;
        const matchesSize =
          filters.size === null || stone.size === filters.size;

        if (isIconSet) {
          // Icon-basierte Steine: ursprüngliche Zuordnung beibehalten
          const matchesVariant =
            filters.variant === null || stone.variant === filters.variant;
          const matchesShape =
            filters.shape === null || stone.shape === filters.shape;

          return matchesColor && matchesSize && matchesVariant && matchesShape;
        }

        // Default-Stones (Theme-Steine):
        // - Variante (Filter) = rund / eckig  -> wirkt auf SHAPE
        // - Umrandung (Filter) = mit / ohne  -> wirkt auf VARIANT (Ring vs voll)
        const matchesVariantAsShape =
          filters.variant === null || stone.shape === filters.variant;

        const effectiveVariantFilter =
          filters.shape === null ? null : filters.shape === 0 ? 1 : 0; // 0 („mit“) => variant 1 (Ring), 1 („ohne“) => variant 0 (voll)

        const matchesShapeAsVariant =
          effectiveVariantFilter === null ||
          (stone.variant ?? 0) === effectiveVariantFilter;

        return (
          matchesColor &&
          matchesSize &&
          matchesVariantAsShape &&
          matchesShapeAsVariant
        );
      });
      setFilteredStones(filteredStones);
    },
    [remainingStones, game]
  );

  const visibleStones = filteredStones.filter(
    (s) => !game?.currentStone || s.key !== game.currentStone.key
  );

  return (
    <Card
      elevation={0}
      sx={{
        mx: { xs: 0, sm: 2, md: 3 },
        mt: { xs: 1.5, sm: 2 },
        mb: { xs: 2, sm: 4 },
        borderRadius: { xs: 0, sm: 3 },
        maxWidth: 700,
        width: "100%",
        // Use theme background on all viewports so dark themes stay dark.
        background: themeConfig.dialog.background,
        border: themeConfig.dialog.border,
      }}
    >
      <CardContent sx={{ p: 0 }}>
        {/* Header Section */}
        <Box
          sx={{
            p: { xs: 2, sm: 3 },
            borderBottom: "2px solid",
            borderColor: "divider",
            bgcolor: "action.hover",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography
              variant="h6"
              fontWeight={700}
              color="primary.main"
              fontSize={{ xs: "1rem", sm: "1.15rem" }}
            >
              {t("gameControl:remainingStonesTitle")}
            </Typography>
            <Chip
              label={Math.max(
                0,
                game?.currentStone
                  ? remainingStones.length - 1
                  : remainingStones.length
              )}
              size="medium"
              sx={{
                fontWeight: 700,
                minWidth: 36,
                bgcolor: "primary.main",
                color: "primary.contrastText",
                opacity: 0.85,
                border: "1px solid",
                borderColor: "primary.main",
              }}
            />
          </Box>

          {/* Integrated Filter Button */}
          <FilterStones
            handleFiltered={handleFiltered}
            stones={stones}
            game={game}
            filteredCount={filteredStones.length}
          />
        </Box>

        {/* Body Section */}
        <Box sx={{ p: { xs: 2, sm: 3 }, pt: { xs: 3, sm: 4 } }}>
          {/* Stones Grid */}
          {visibleStones.length > 0 ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(4, 1fr)",
                  sm: "repeat(5, 1fr)",
                  md: "repeat(6, 1fr)",
                },
                gap: { xs: 2, sm: 2.5, md: 3 },
                justifyItems: "center",
                p: { xs: 2, sm: 3 },
                bgcolor: "action.hover",
                borderRadius: { xs: 0, sm: 2 },
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              {visibleStones.map((stone, index) => {
                const isIconSet =
                  game?.iconSet !== null && game?.iconSet !== undefined;
                return (
                  <Box
                    key={`remainingStone-${index}`}
                    sx={{
                      width: { xs: 60, sm: 70, md: 80 },
                      height: { xs: 60, sm: 70, md: 80 },
                      background: isIconSet
                        ? "background.paper"
                        : themeConfig.tile.background,
                      border: isIconSet ? "2px solid" : themeConfig.tile.border,
                      borderColor: isIconSet ? "divider" : undefined,
                      boxShadow: isIconSet ? 2 : themeConfig.tile.boxShadow,
                      borderRadius: isIconSet ? "50%" : 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                      transition: "all 0.25s ease-out",
                      "&:hover": {
                        transform: "translateY(-4px) scale(1.05)",
                        boxShadow: isIconSet
                          ? "0 4px 16px rgba(0,0,0,0.15)"
                          : "0 6px 20px rgba(0,0,0,0.3)",
                        borderColor: isIconSet ? "primary.main" : undefined,
                      },
                    }}
                  >
                    <StoneControl stone={stone} iconSet={game?.iconSet} />
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Box
              sx={{
                textAlign: "center",
                py: 6,
                px: 3,
                bgcolor: "action.hover",
                borderRadius: { xs: 0, sm: 2 },
                border: "2px dashed",
                borderColor: "divider",
              }}
            >
              <FilterIcon
                sx={{
                  fontSize: 48,
                  color: "text.secondary",
                  opacity: 0.5,
                  mb: 2,
                }}
              />
              <Typography variant="body2" color="text.primary" fontWeight={500}>
                {t("gameControl:noStonesMatchFilter")}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                {t("gameControl:adjustFilters")}
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
